import { useId, useState } from 'react';
import DatePicker from 'react-datepicker';
import { Trans, useTranslation } from 'react-i18next';
import 'react-datepicker/dist/react-datepicker.css';
import { submitProofOfStatus } from '../../sagas/sagaActions';
import checkoutStyles from '../../shared/checkoutLayout.module.scss';
import { Disclosure } from '../../shared/Disclosure';
import { eduEligibilityLink, legalLinks } from '../../shared/data';
import {
  CalendarIcon,
  ChevronDownIcon,
  EditIcon,
  UploadIcon,
} from '../../shared/icons';
import {
  type EduForm,
  setEduStatus,
  setForm,
  setSubmitted,
} from '../../store/eduSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import styles from './ProofOfStatus.module.scss';
import { isEduFormValid } from './validators';

function formatGraduationDate(iso: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function TextField({
  label,
  placeholder,
  value,
  error,
  onChange,
}: {
  label: string;
  placeholder?: string;
  value: string;
  error?: string | null;
  onChange: (value: string) => void;
}) {
  return (
    <label className={checkoutStyles.formField}>
      <span className={checkoutStyles.formLabel}>{label}</span>
      <div className={checkoutStyles.control}>
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${checkoutStyles.controlBox} ${error ? checkoutStyles.error : ''}`}
        />
      </div>
      {error && <div className={checkoutStyles.fieldError}>{error}</div>}
    </label>
  );
}

export function ProofOfStatus() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { form, errors } = useAppSelector((state) => state.eduStatus);
  const countries = useAppSelector((state) => state.app.countries);
  const [file, setFile] = useState<File | null>(null);
  const dateFieldId = useId();

  const set = (patch: Partial<EduForm>) => dispatch(setForm(patch));

  const onFileSelect = (selected: File | null) => {
    if (!selected) return;
    setFile(selected);
    const reader = new FileReader();
    reader.onload = () => {
      set({
        imageProof: {
          url: reader.result as string,
          name: selected.name,
          type: selected.type,
          size: selected.size,
        },
      });
    };
    reader.readAsDataURL(selected);
  };

  return (
    <section className={checkoutStyles.step}>
      <h2 className={checkoutStyles.stepTitle}>
        {t('Proof of student status')}
      </h2>

      <div className={styles.form}>
        <div className={checkoutStyles.formField}>
          <label className={checkoutStyles.formLabel} htmlFor={dateFieldId}>
            {t('Expected graduation date')}
          </label>
          <div className={checkoutStyles.control}>
            <DatePicker
              id={dateFieldId}
              selected={
                form.expectedGraduationDate
                  ? new Date(form.expectedGraduationDate)
                  : null
              }
              onChange={(date) => {
                if (!date) return;
                set({
                  expectedGraduationDate: date.toLocaleDateString('fr-CA', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  }),
                });
              }}
              filterDate={(date) => date > new Date()}
              dateFormat="d MMMM yyyy"
              placeholderText={t('Choose date')}
              wrapperClassName={styles.datepickerWrapper}
              className={`${checkoutStyles.controlBox} ${checkoutStyles.controlBoxIcon} ${errors.expectedGraduationDate ? checkoutStyles.error : ''}`}
            />
            <CalendarIcon
              className={`${checkoutStyles.selectChevron} ${checkoutStyles.selectChevronOverlay}`}
            />
          </div>
          {errors.expectedGraduationDate && (
            <div className={checkoutStyles.fieldError}>
              {errors.expectedGraduationDate}
            </div>
          )}
        </div>

        <TextField
          label={t('Name of academic institution')}
          placeholder={t('Oxford Brookes University')}
          value={form.institutionName}
          error={errors.institutionName}
          onChange={(value) => set({ institutionName: value })}
        />
        <TextField
          label={t('City of academic institution')}
          placeholder={t('Oxford')}
          value={form.institutionCity}
          error={errors.institutionCity}
          onChange={(value) => set({ institutionCity: value })}
        />

        <label className={checkoutStyles.formField}>
          <span className={checkoutStyles.formLabel}>
            {t('Country of academic institution')}
          </span>
          <div className={checkoutStyles.control}>
            <select
              className={`${checkoutStyles.controlBox} ${checkoutStyles.selectBox} ${checkoutStyles.controlBoxIcon} ${checkoutStyles.nativeSelect} ${errors.institutionCountry ? checkoutStyles.error : ''}`}
              value={form.institutionCountry}
              onChange={(e) => set({ institutionCountry: e.target.value })}
            >
              <option value="" disabled>
                {t('Please select')}
              </option>
              {countries?.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
            <ChevronDownIcon
              className={`${checkoutStyles.selectChevron} ${checkoutStyles.selectChevronOverlay}`}
            />
          </div>
          {errors.institutionCountry && (
            <div className={checkoutStyles.fieldError}>
              {errors.institutionCountry}
            </div>
          )}
        </label>

        <label className={checkoutStyles.formField}>
          <span className={checkoutStyles.formLabel}>
            {t('Proof of student status document (jpg, png, pdf)')}
          </span>
          <div className={checkoutStyles.control}>
            <div
              className={`${checkoutStyles.controlBox} ${checkoutStyles.controlBoxIcon} ${styles.fileBox} ${errors.imageProof ? checkoutStyles.error : ''}`}
            >
              <span className={file ? styles.fileName : styles.filePlaceholder}>
                {file ? file.name : t('Choose file')}
              </span>
              <input
                type="file"
                aria-label={t(
                  'Proof of student status document (jpg, png, pdf)',
                )}
                accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                className={styles.fileInput}
                onChange={(e) => onFileSelect(e.target.files?.[0] ?? null)}
              />
            </div>
            <UploadIcon
              className={`${checkoutStyles.selectChevron} ${checkoutStyles.selectChevronOverlay}`}
            />
          </div>
          {errors.imageProof && (
            <div className={checkoutStyles.fieldError}>{errors.imageProof}</div>
          )}
        </label>

        {/* biome-ignore-start lint/a11y/useAnchorContent: Trans fills the anchor at render time */}
        <Disclosure title={t('How we review your documents')}>
          {t(
            'To help review documents submitted for student discounts, we use an artificial intelligence (AI) system that assists our team by providing an indicative assessment of a document’s apparent validity. Final decisions are always made by a human reviewer. You may contact us with questions or to exercise your data protection rights by reviewing our',
          )}{' '}
          <Trans
            i18nKey="<policy>Privacy Policy</policy>."
            components={{
              policy: (
                <a href={legalLinks.privacy} target="_blank" rel="noreferrer" />
              ),
            }}
          />
        </Disclosure>
        {/* biome-ignore-end lint/a11y/useAnchorContent: Trans fills the anchor at render time */}

        <TextField
          label={t('Additional information (optional)')}
          value={form.notes}
          onChange={(value) => set({ notes: value })}
        />
        {/* biome-ignore-start lint/a11y/useAnchorContent: Trans fills the anchor at render time */}
        <Disclosure
          title={t('What is considered valid evidence of current enrollment?')}
        >
          <ul>
            <li>
              {t(
                'A valid, dated student ID which includes your name and photo',
              )}
            </li>
            <li>
              {t(
                'An academic document which states your enrolment status, your name, educational institution and dates of enrolment. Examples include a transcript of records, tuition bill, current student report card or progress report, dated current class schedule, etc.',
              )}
            </li>
          </ul>
          <div>
            <Trans
              i18nKey="You can find <eligibility>more details here.</eligibility>"
              components={{
                eligibility: (
                  <a
                    href={eduEligibilityLink}
                    target="_blank"
                    rel="noreferrer"
                  />
                ),
              }}
            />
          </div>
        </Disclosure>
        {/* biome-ignore-end lint/a11y/useAnchorContent: Trans fills the anchor at render time */}
      </div>

      <div>
        <button
          type="button"
          disabled={!isEduFormValid(form, errors)}
          className={checkoutStyles.mailBtn}
          onClick={() => dispatch(submitProofOfStatus())}
        >
          {t('Continue with checkout')}
        </button>
      </div>
    </section>
  );
}

export function ProofOfStatusSummary({
  approved,
  underReview,
}: {
  approved: boolean;
  underReview: boolean;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { form } = useAppSelector((state) => state.eduStatus);
  const countries = useAppSelector((state) => state.app.countries);

  const onEdit = () => {
    dispatch(setEduStatus(null));
    dispatch(setSubmitted(false));
  };

  let statusMessage: string | null = null;
  if (approved) {
    statusMessage = t('Your proof is approved.');
  } else if (underReview) {
    statusMessage = t('Your proof is under review.');
  }

  return (
    <section className={checkoutStyles.step}>
      <div className={checkoutStyles.stepHead}>
        <h2 className={checkoutStyles.stepTitle}>
          {t('Proof of student status')}
        </h2>
        <button
          type="button"
          className={checkoutStyles.editBtn}
          onClick={onEdit}
        >
          <EditIcon className={checkoutStyles.editIcon} />
          {t('edit')}
        </button>
      </div>

      {statusMessage ? (
        <div>{statusMessage}</div>
      ) : (
        <dl className={styles.summaryList}>
          <div className={styles.summaryRow}>
            <dt>{t('Expected graduation date')}</dt>
            <dd>{formatGraduationDate(form.expectedGraduationDate)}</dd>
          </div>
          <div className={styles.summaryRow}>
            <dt>{t('Name of academic institution')}</dt>
            <dd>{form.institutionName}</dd>
          </div>
          <div className={styles.summaryRow}>
            <dt>{t('City of academic institution')}</dt>
            <dd>{form.institutionCity}</dd>
          </div>
          <div className={styles.summaryRow}>
            <dt>{t('Country of academic institution')}</dt>
            <dd>
              {countries?.find(
                (country) => country.code === form.institutionCountry,
              )?.name ?? form.institutionCountry}
            </dd>
          </div>
          <div className={styles.summaryRow}>
            <dt>{t('Proof of student status document (jpg, png, pdf)')}</dt>
            <dd>{form.imageProof?.name}</dd>
          </div>
        </dl>
      )}
    </section>
  );
}
